// filepath: src/pages/matrimony/components/sectionForms/SummaryEditForm.tsx
import React from "react";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { Plus, X, Trash2 } from "lucide-react";
import { commonInputProps } from "src/pages/matrimony/pages/matrimonyForm.ui";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";
import type { SummaryState } from "src/types/marriageProfileSectionEdit.types";
import type { BaseSectionFormProps, SummaryPhotoProps } from "./sectionFormProps";

type Props = BaseSectionFormProps &
  SummaryPhotoProps & {
    values: SummaryState;
  };

const SummaryEditForm: React.FC<Props> = ({
  values,
  errors,
  setField,
  photoPreviewUrl,
  onPhotoChange,
  serverImages,
  serverImageUrls,
  deletingImageId,
  onDeleteImage,
  additionalPhotoFiles,
  additionalPreviewUrls,
  onAdditionalFileChange,
  availableSlots,
  totalImageCount,
  maxAdditional,
  maxPhotoBytes,
  showErrorToast,
}) => (
  <>
    <FormControl isInvalid={!!errors.fullName} isRequired>
      <FormLabel>{FIELD_LABELS.FULL_NAME}</FormLabel>
      <Input
        {...commonInputProps}
        value={values.fullName || ""}
        onChange={(e) => setField("fullName", e.target.value)}
        placeholder="Enter the full name"
      />
      <FormErrorMessage>{errors.fullName}</FormErrorMessage>
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.ABOUT}</FormLabel>
      <Input
        {...commonInputProps}
        value={values.about || ""}
        onChange={(e) => setField("about", e.target.value)}
        placeholder="Write something about yourself"
      />
    </FormControl>

    <FormControl>
      <FormLabel>{FIELD_LABELS.PROFILE_PHOTO}</FormLabel>
      <Input
        {...commonInputProps}
        type="file"
        p={2}
        accept="image/jpeg,image/jpg,image/png"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          onPhotoChange(file);
        }}
      />
      {photoPreviewUrl ? (
        <Image
          mt={3}
          src={photoPreviewUrl}
          alt="Selected profile photo"
          maxH="220px"
          borderRadius="12px"
          objectFit="cover"
        />
      ) : null}
    </FormControl>

    {/* ── Additional Photos ───────────────────────────── */}
    <FormControl>
      <HStack justify="space-between" mb={2}>
        <FormLabel mb={0}>Additional Photos (Max {maxAdditional})</FormLabel>
        <Text fontSize="xs" fontWeight="600" color="#4A2C2A">
          {totalImageCount}/{maxAdditional}
        </Text>
      </HStack>

      {/* Existing server images */}
      {serverImages.length > 0 && (
        <>
          <Text fontSize="xs" color="#8a7b7b" mb={2}>Uploaded Photos</Text>
          <SimpleGrid columns={4} spacing={3} mb={4}>
            {serverImages.map((img) => (
              <Box
                key={img.id}
                position="relative"
                w="full"
                h="0"
                pb="100%"
                borderRadius="12px"
                border="1px solid #E9DFDF"
                bg="#F8F9FA"
                overflow="hidden"
              >
                {serverImageUrls[img.id] ? (
                  <Image
                    src={serverImageUrls[img.id]}
                    alt={`Photo ${img.id}`}
                    position="absolute"
                    top={0}
                    left={0}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                ) : (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    w="100%"
                    h="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="2xs" color="#aaa">Loading…</Text>
                  </Box>
                )}
                <IconButton
                  aria-label="Delete photo"
                  icon={<Trash2 size={14} />}
                  size="xs"
                  position="absolute"
                  top={1}
                  right={1}
                  borderRadius="full"
                  bg="rgba(220,53,69,0.85)"
                  color="white"
                  _hover={{ bg: "rgba(220,53,69,1)" }}
                  isLoading={deletingImageId === img.id}
                  onClick={() => onDeleteImage(img.id)}
                />
              </Box>
            ))}
          </SimpleGrid>
        </>
      )}

      {/* New upload slots */}
      {availableSlots > 0 && (
        <>
          <Text fontSize="xs" color="#8a7b7b" mb={2}>
            Add New Photos ({availableSlots} slot{availableSlots > 1 ? "s" : ""} available)
          </Text>
          <SimpleGrid columns={4} spacing={3}>
            {Array.from({ length: availableSlots }).map((_, idx) => {
              const file = additionalPhotoFiles[idx] ?? null;
              const preview = additionalPreviewUrls[idx] ?? "";
              return (
                <Box
                  key={`new-${idx}`}
                  position="relative"
                  w="full"
                  h="0"
                  pb="100%"
                  borderRadius="12px"
                  border="1px dashed rgba(108,117,125,0.4)"
                  bg="#F8F9FA"
                  overflow="hidden"
                >
                  {file && preview ? (
                    <>
                      <Image
                        src={preview}
                        alt={`New photo ${idx + 1}`}
                        position="absolute"
                        top={0}
                        left={0}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                      <IconButton
                        aria-label="Remove photo"
                        icon={<X size={14} />}
                        size="xs"
                        position="absolute"
                        top={1}
                        right={1}
                        borderRadius="full"
                        bg="rgba(0,0,0,0.55)"
                        color="white"
                        _hover={{ bg: "rgba(0,0,0,0.75)" }}
                        onClick={() => {
                          const next = [...additionalPhotoFiles];
                          next[idx] = null;
                          const compacted = next.filter(Boolean) as (File | null)[];
                          while (compacted.length < availableSlots) compacted.push(null);
                          if (preview) URL.revokeObjectURL(preview);
                          const previews = compacted.map((f) => (f ? URL.createObjectURL(f) : ""));
                          onAdditionalFileChange(compacted, previews);
                        }}
                      />
                    </>
                  ) : (
                    <Box
                      as="label"
                      cursor="pointer"
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      _hover={{ bg: "rgba(74,44,42,0.04)" }}
                    >
                      <Input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        display="none"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          if (f && f.size > maxPhotoBytes) {
                            showErrorToast("Each photo must be 1 MB or less");
                            e.currentTarget.value = "";
                            return;
                          }
                          const next = [...additionalPhotoFiles];
                          while (next.length < availableSlots) next.push(null);
                          next[idx] = f;
                          const previews = next.map((fl) => (fl ? URL.createObjectURL(fl) : ""));
                          onAdditionalFileChange(next, previews);
                          e.currentTarget.value = "";
                        }}
                      />
                      <Plus size={20} color="#6C757D" />
                      <Text fontSize="2xs" color="#6C757D" mt={1}>ADD</Text>
                    </Box>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>
        </>
      )}

      {availableSlots === 0 && serverImages.length >= maxAdditional && (
        <Text fontSize="xs" color="#aaa" mt={1}>
          Maximum {maxAdditional} additional photos reached. Delete an existing photo to upload a new one.
        </Text>
      )}
    </FormControl>
  </>
);

export default SummaryEditForm;
